"use client";

import { useLang } from "@/context/lang";
import { useUpper } from "@/lib/upper";
import { motion } from "motion/react";
import { Type, Mic, Download, ArrowRight } from "lucide-react";

export function HowItWorks() {
  const { T } = useLang();
  const U = useUpper();

  const steps = [
    {
      num: 1,
      icon: Type,
      title: T("how.step1.title"),
      desc: T("how.step1.desc"),
      visual: (
        <div className="bg-neutral-50 dark:bg-slate-900/50 rounded-xl p-4 border border-neutral-200 dark:border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          </div>
          <div className="space-y-2">
            <div className="h-2 bg-cyan-500/20 rounded w-full" />
            <div className="h-2 bg-cyan-500/15 rounded w-4/5" />
            <div className="h-2 bg-cyan-500/10 rounded w-3/5" />
            <motion.div
              className="h-4 w-px bg-cyan-400"
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          </div>
        </div>
      ),
    },
    {
      num: 2,
      icon: Mic,
      title: T("how.step2.title"),
      desc: T("how.step2.desc"),
      visual: (
        <div className="bg-neutral-50 dark:bg-slate-900/50 rounded-xl p-4 border border-neutral-200 dark:border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white text-[10px] font-bold">DE</div>
            <div>
              <div className="text-xs font-semibold text-neutral-900 dark:text-white">Defne</div>
              <div className="text-[10px] text-neutral-400 dark:text-slate-500">Turkish &middot; Female</div>
            </div>
            <div className="ml-auto w-4 h-4 rounded-full border-2 border-cyan-500 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-cyan-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {["Stability: 75%", "Speed: 1.0x"].map((s) => (
              <div key={s} className="bg-neutral-100 dark:bg-white/5 rounded-lg px-2 py-1.5">
                <div className="text-[9px] text-neutral-400 dark:text-slate-500">{s.split(":")[0]}</div>
                <div className="text-[10px] text-cyan-600 dark:text-cyan-300 font-bold">{s.split(":")[1]}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      num: 3,
      icon: Download,
      title: T("how.step3.title"),
      desc: T("how.step3.desc"),
      visual: (
        <div className="bg-neutral-50 dark:bg-slate-900/50 rounded-xl p-4 border border-neutral-200 dark:border-white/10">
          <div className="flex items-center gap-[2px] h-10 mb-3">
            {Array.from({ length: 30 }).map((_, i) => (
              <motion.div
                key={i}
                className="flex-1 bg-gradient-to-t from-cyan-500 to-teal-400 rounded-sm"
                animate={{ height: [3, (i * 11 + 5) % 32 + 6, 3] }}
                transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.04, ease: "easeInOut" }}
              />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center">
                <motion.div animate={{ rotate: [0, 0, 0] }} className="text-neutral-900 dark:text-white text-[10px]">&#9654;</motion.div>
              </div>
              <span className="text-[10px] text-neutral-400 dark:text-slate-500 font-mono">0:08 / 0:12</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="px-2 py-1 rounded bg-cyan-500/10 text-[9px] text-cyan-600 dark:text-cyan-300 font-bold">MP3</div>
              <div className="px-2 py-1 rounded bg-neutral-100 dark:bg-white/5 text-[9px] text-neutral-400 dark:text-slate-500 font-bold">WAV</div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section id="nasil-calisir" className="relative py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-cyan-100 dark:bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-teal-100/20 dark:bg-teal-500/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
            className="inline-block text-sm font-bold text-cyan-600 dark:text-cyan-300 tracking-wider bg-cyan-500/10 px-4 py-1.5 rounded-full border border-cyan-500/20 mb-4"
          >
            {U(T("how.subtitle"))}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white">
            {T("how.title")}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-4">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2, duration: 0.7, type: "spring", stiffness: 80 }}
                whileHover={{ y: -6, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                className="relative group"
              >
                {/* Arrow between cards (desktop) */}
                {i < 2 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.2 + 0.4, type: "spring", stiffness: 200 }}
                    className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-neutral-100 dark:bg-white/5 border border-neutral-200 dark:border-white/10 items-center justify-center"
                  >
                    <motion.div animate={{ x: [0, 3, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                      <ArrowRight size={14} className="text-cyan-600 dark:text-cyan-300" />
                    </motion.div>
                  </motion.div>
                )}

                <div className="bg-neutral-50 dark:bg-white/5 backdrop-blur-sm border border-neutral-200 dark:border-white/10 rounded-2xl p-6 hover:border-cyan-800/50 hover:shadow-xl hover:shadow-cyan-500/5 transition-all duration-500 h-full">
                  {/* Step number + icon row */}
                  <div className="flex items-center gap-3 mb-5">
                    <motion.div
                      initial={{ rotate: -180, scale: 0 }}
                      whileInView={{ rotate: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.2 + 0.3, type: "spring", stiffness: 200, damping: 15 }}
                      className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white font-extrabold text-sm shadow-lg shadow-cyan-500/20"
                    >
                      {step.num}
                    </motion.div>
                    <div className="w-10 h-10 rounded-xl bg-neutral-100 dark:bg-white/5 border border-neutral-200 dark:border-white/10 flex items-center justify-center group-hover:border-cyan-800/60 transition-colors">
                      <Icon size={18} className="text-cyan-600 dark:text-cyan-300" />
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-neutral-800 to-transparent" />
                  </div>

                  {/* Title & desc */}
                  <h3 className="text-neutral-900 dark:text-white font-bold text-lg mb-2">{step.title}</h3>
                  <p className="text-neutral-400 dark:text-slate-500 text-sm leading-relaxed mb-5">{step.desc}</p>

                  {/* Mini visual */}
                  {step.visual}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
